/**
 * The question bank (Treatment Plan spec v1.4, Question Bank tab). MCOS owns
 * the wording. The medication answers (GQ-M) reach patients as instant answers
 * only after Dr. Joe approves them (spec open decision 2); until then those
 * questions go to the provider. Medical director and platform owner.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Button, Card, ErrorState, LoadingRows, PageHeader, Pill } from '../components/ui';

interface Entry {
  id: string;
  topic: string;
  surface: string;
  question: string;
  template: string | null;
  alwaysEscalate: boolean;
  needsApproval: boolean;
  approvedBy: string | null;
  approvedAt: string | null;
}

export function QuestionBankPage() {
  const qc = useQueryClient();
  const bank = useQuery({ queryKey: ['question-bank'], queryFn: () => api.get<{ entries: Entry[] }>('/admin/question-bank') });
  const approve = useMutation({
    mutationFn: (id: string) => api.post<{ entries: Entry[] }>(`/admin/question-bank/${id}/approve`),
    onSuccess: (data) => qc.setQueryData(['question-bank'], data),
  });
  const entries = bank.data?.entries ?? [];
  const waiting = entries.filter((e) => e.needsApproval && !e.approvedAt);
  const plan = entries.filter((e) => e.surface === 'plan');
  const messages = entries.filter((e) => e.surface !== 'plan');

  const row = (e: Entry, canApprove = false) => (
    <li key={e.id} className="py-3" data-testid={`bank-${e.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 max-w-[70ch]">
          <p className="text-[14px] font-semibold text-navy">
            {e.question} <span className="font-normal text-[#5b6b7c]">{e.id}</span>
          </p>
          {e.template ? <p className="mt-1 text-[13px] leading-relaxed text-[#4d6072]">{e.template}</p> : <p className="mt-1 text-[13px] text-[#5b6b7c]">Always goes to the care team.</p>}
          {e.approvedBy && <p className="mt-1 text-[12px] text-[#5b6b7c]">Approved by {e.approvedBy} on {e.approvedAt?.slice(0, 10)}</p>}
        </div>
        <div className="flex items-center gap-2">
          {e.needsApproval && !e.approvedAt && (
            <>
              <Pill className="bg-risk-yellow-soft text-risk-yellow">needs approval</Pill>
              {canApprove && <Button size="sm" loading={approve.isPending} onClick={() => approve.mutate(e.id)}>Approve</Button>}
            </>
          )}
          {e.needsApproval && e.approvedAt && <Pill className="bg-mint text-forest">approved</Pill>}
          {(e.alwaysEscalate || !e.template) && <Pill className="bg-[#e6eef7] text-navy">care team</Pill>}
        </div>
      </div>
    </li>
  );

  return (
    <div className="mx-auto max-w-[980px]">
      <PageHeader title="Question bank" subtitle="The instant answers patients see. Medication answers show only once Dr. Joe approves them." />
      {bank.isLoading && <LoadingRows rows={6} />}
      {bank.error && <ErrorState error={bank.error} retry={() => void bank.refetch()} />}
      {approve.error && <p className="mb-3 text-sm text-red-600" role="alert">{approve.error instanceof Error ? approve.error.message : 'Could not approve'}</p>}
      {bank.data && (
        <div className="space-y-5">
          {waiting.length > 0 && (
            <Card title="Waiting for approval" subtitle="Read each answer as a patient would. Until approved, these questions go to the provider.">
              <ul className="divide-y divide-hairline">{waiting.map((e) => row(e, true))}</ul>
            </Card>
          )}
          <Card title="On the plan page" subtitle="I have a question, phase 4."><ul className="divide-y divide-hairline">{plan.map((e) => row(e))}</ul></Card>
          <Card title="Message Care Team" subtitle="Used by messaging, phase 6."><ul className="divide-y divide-hairline">{messages.map((e) => row(e))}</ul></Card>
        </div>
      )}
    </div>
  );
}
